import { useState } from "react";
import WhiteLabelChatPanel from "./WhiteLabelChatPanel";
import DevTenantDebug from "../components/DevTenantDebug";
import { useClinicProfile } from "../hooks/useClinicProfile";
import { getVetcanApiBaseUrl } from "../lib/apiBase";
import { type WhiteLabelTenantConfig } from "../lib/tenantConfig";

function getClinicInitials(clinicName: string) {
  const words = clinicName
    .split(/\s+/)
    .map((word) => word.trim())
    .filter(Boolean);
  const firstTwo = words.slice(0, 2).map((word) => word[0] || "");
  return firstTwo.join("").toUpperCase() || "CL";
}

export default function WhiteLabelChatPage({
  tenant,
  tenantKey,
}: {
  tenant: WhiteLabelTenantConfig;
  tenantKey: string;
}) {
  const {
    tenantKey: resolvedTenantKey,
    profile,
    source,
    isLoading: profileLoading,
    devNotice,
    lastFetchStatus,
    lastFetchAt,
  } = useClinicProfile({ tenantKey });
  const [logoVisible, setLogoVisible] = useState(Boolean(tenant.logoPath));

  const clinicName = profile.displayName;
  const supportPhone = profile.supportPhone;
  const renewalsPhone = profile.renewalsPhone;
  const supportTagline = tenant.supportTagline || "Clinic scheduling and support";
  const runtimeHost =
    typeof window !== "undefined" ? window.location.host : resolvedTenantKey;

  const embedMode =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("embed") === "1";

  if (embedMode) {
    return (
      <div className="min-h-screen bg-white p-3 sm:p-4">
        <WhiteLabelChatPanel
          tenant={tenant}
          clinicProfile={profile}
          profileLoading={profileLoading}
          tenantKey={resolvedTenantKey}
          mode="embed"
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-white to-emerald-50 text-slate-900">
      <header className="border-b border-emerald-100 bg-white">
        <div className="mx-auto flex w-full max-w-5xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3">
            {logoVisible && tenant.logoPath ? (
              <img
                src={tenant.logoPath}
                alt={`${clinicName} logo`}
                className="h-10 w-auto"
                onError={() => {
                  setLogoVisible(false);
                }}
              />
            ) : (
              <div
                aria-label={`${clinicName} logo fallback`}
                className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-100 text-xs font-semibold text-emerald-800"
              >
                {getClinicInitials(clinicName)}
              </div>
            )}
            <div>
              <h1 className="text-xl font-semibold text-emerald-950">
                {clinicName} Chat
              </h1>
              <p className="text-xs text-slate-600">{supportTagline}</p>
            </div>
          </div>
          <a
            href="/"
            className="rounded-full border border-emerald-200 px-4 py-2 text-sm font-semibold text-emerald-900 transition hover:bg-emerald-50"
          >
            Back to home
          </a>
        </div>
      </header>
      <main className="mx-auto w-full max-w-5xl px-6 py-8">
        {import.meta.env.DEV && devNotice ? (
          <div className="mb-5 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            {devNotice}
          </div>
        ) : null}
        <div className="mb-5 rounded-2xl border border-emerald-100 bg-white px-5 py-4 text-sm text-slate-700 shadow-sm">
          <p className="font-semibold text-slate-900">Need immediate help?</p>
          <p className="mt-1">
            Call or text 24/7: {supportPhone}. Renew by telephone (no video):{" "}
            {renewalsPhone}.
          </p>
        </div>
        <WhiteLabelChatPanel
          tenant={tenant}
          clinicProfile={profile}
          profileLoading={profileLoading}
          tenantKey={resolvedTenantKey}
          mode="full"
        />
      </main>
      {import.meta.env.DEV ? (
        <DevTenantDebug
          host={runtimeHost}
          tenantKey={resolvedTenantKey}
          source={source}
          clinicName={clinicName}
          supportPhone={supportPhone}
          apiBase={getVetcanApiBaseUrl()}
          lastFetchStatus={lastFetchStatus}
          lastFetchAt={lastFetchAt}
        />
      ) : null}
    </div>
  );
}
